import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FiMic, FiSquare, FiPlay, FiPause, FiSave, FiTrash2, FiUpload } from 'react-icons/fi'
import { motion } from 'framer-motion'
import { useRecorder } from './useRecorder'
import { useSession } from '../../app/useSession'
import { supabase } from '../../app/supabaseClient'
import { getQueryParam } from '../../lib/query'

// Utility functions
const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const getFileExtension = (mimeType: string): string => {
  if (mimeType.includes('webm')) return 'webm'
  if (mimeType.includes('mp4')) return 'mp4'
  if (mimeType.includes('wav')) return 'wav'
  return 'webm'
}

interface UploadState {
  isUploading: boolean
  isUploaded: boolean
  error: string | null
  signedUrl: string | null
}

// Best-effort origin for postMessage back to parent
const getParentOrigin = (): string => {
  try {
    if (document.referrer) return new URL(document.referrer).origin
  } catch {}
  return '*' // fall back if unknown; you can harden later
}

export default function AudioRecorderV2() {
  const { session } = useSession()
  const recorder = useRecorder()
  const containerRef = useRef<HTMLDivElement>(null)
  const parentOriginRef = useRef<string>(getParentOrigin())

  // URL params
  const lessonNoteId = getQueryParam('lesson_note_id')
  const lessonToolId = getQueryParam('lesson_tool_id')
  const userId = getQueryParam('userId')

  const [upload, setUpload] = useState<UploadState>({
    isUploading: false,
    isUploaded: false,
    error: null,
    signedUrl: null
  })

  // --- Session handshake: non-blocking request + retries ---
  useEffect(() => {
    const request = () => {
      try {
        window.parent?.postMessage({ type: 'mms:requestSession' }, parentOriginRef.current)
      } catch {}
    }

    // fire immediately, then retry a few times while we wait for a session
    request()
    const retryId = setInterval(request, 1500)

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      if (s?.user?.id) {
        clearInterval(retryId)
      }
    })

    return () => {
      clearInterval(retryId)
      sub?.subscription?.unsubscribe?.()
    }
  }, [])

  // Listen for session messages from parent (and optional ping)
  useEffect(() => {
    const onMessage = async (e: MessageEvent) => {
      // Optional hardening:
      // if (!e.origin.endsWith('mymusicstudio.app')) return

      if (e.data?.type === 'mms:setSession') {
        const { access_token, refresh_token } = e.data.payload || {}
        if (access_token && refresh_token) {
          try {
            await supabase.auth.setSession({ access_token, refresh_token })
            // console.debug('[Recorder] Session set from parent')
          } catch (err) {
            console.error('[Recorder] Error setting session from parent:', err)
          }
        }
      } else if (e.data?.type === 'mms:ping') {
        try {
          window.parent?.postMessage({ type: 'mms:pong' }, e.origin)
        } catch {}
      }
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Post resize messages
  const postResize = useCallback(() => {
    if (containerRef.current) {
      const height = Math.max(containerRef.current.scrollHeight, 120)
      try {
        window.parent?.postMessage({ type: 'resize', height }, parentOriginRef.current)
      } catch {}
    }
  }, [])

  // Initial resize and when layout changes
  useEffect(() => {
    const timer = setTimeout(postResize, 100)
    return () => clearTimeout(timer)
  }, [postResize, recorder.state, recorder.blob, upload.isUploaded])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return

      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault()
        if (recorder.state === 'idle') {
          recorder.start()
        } else if (recorder.state === 'recording') {
          recorder.stop()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [recorder.state, recorder.start, recorder.stop])

  // Upload with retry logic
  const uploadRecording = useCallback(async () => {
    // Basic guards
    if (!recorder.blob) {
      setUpload(prev => ({ ...prev, error: 'No recording to save yet. Press Stop first.' }))
      return
    }
    if (!lessonNoteId) {
      setUpload(prev => ({ ...prev, error: 'Recorder not set up correctly (missing lesson note). Please refresh.' }))
      return
    }
    if (!userId) {
      setUpload(prev => ({ ...prev, error: 'Recorder not set up correctly (missing user). Please refresh.' }))
      return
    }
    if (!lessonToolId) {
      setUpload(prev => ({ ...prev, error: 'Recorder not set up correctly (missing tool). Please refresh.' }))
      return
    }
    if (recorder.blob.size === 0) {
      setUpload(prev => ({ ...prev, error: 'Empty recording. Please record again.' }))
      return
    }
    if (recorder.blob.size > 20 * 1024 * 1024) {
      setUpload(prev => ({ ...prev, error: 'File size exceeds 20MB limit' }))
      return
    }

    // Ensure tool iframe has a Supabase session
    const { data: su } = await supabase.auth.getUser()
    if (!su?.user?.id) {
      setUpload(prev => ({ ...prev, error: 'Not authenticated in tool iframe. Please refresh.' }))
      try { window.parent?.postMessage({ type: 'mms:requestSession' }, parentOriginRef.current) } catch {}
      return
    }

    setUpload(prev => ({ ...prev, isUploading: true, error: null }))

    // One file per tool under the note folder
    const ext = getFileExtension(recorder.mimeType)
    const objectKey = `${lessonNoteId}/${lessonToolId}.${ext}`

    // Optional retry for transient errors
    const maxRetries = 3
    const baseDelay = 500

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log('[Recorder] Upload attempt', attempt + 1, {
          queryUserId: userId,
          sessionUser: su.user.id,
          lessonNoteId,
          objectKey,
          size: recorder.blob.size,
          mime: recorder.mimeType,
        })

        // Upload and allow overwrite (one file per tool)
        const { error: uploadError } = await supabase.storage
          .from('user-audio')
          .upload(objectKey, recorder.blob, {
            contentType: recorder.mimeType,
            upsert: true,
          })
        if (uploadError) throw uploadError

        // Signed URL (1h) for inline preview
        const { data: signedData, error: signError } = await supabase.storage
          .from('user-audio')
          .createSignedUrl(objectKey, 3600)
        if (signError) throw signError

        // (Optional) notify parent
        try {
          window.parent?.postMessage({
            type: 'mms:toolEvent',
            event: 'audio-uploaded',
            payload: {
              bucket: 'user-audio',
              key: objectKey,
              mimeType: recorder.mimeType,
              sizeBytes: recorder.blob.size,
              durationMs: recorder.duration,
              lesson_tool_id: lessonToolId
            }
          }, parentOriginRef.current)
        } catch {}

        setUpload(prev => ({
          ...prev,
          isUploading: false,
          isUploaded: true,
          signedUrl: signedData.signedUrl,
        }))
        console.log('[Recorder] Upload success', { objectKey })
        return

      } catch (err: any) {
        console.error(`[Recorder] Upload attempt ${attempt + 1} failed:`, err)
        if (attempt === maxRetries - 1) {
          setUpload(prev => ({
            ...prev,
            isUploading: false,
            error: err?.message || 'Upload failed',
          }))
          return
        }
        await new Promise(r => setTimeout(r, baseDelay * Math.pow(2, attempt)))
      }
    }
  }, [recorder.blob, recorder.mimeType, recorder.duration, lessonNoteId, lessonToolId, userId])

  // Reset upload state when starting new recording
  useEffect(() => {
    if (recorder.state === 'recording' && upload.isUploaded) {
      setUpload({
        isUploading: false,
        isUploaded: false,
        error: null,
        signedUrl: null
      })
    }
  }, [recorder.state, upload.isUploaded])

    // 🔎 Load an existing saved recording on mount (and when IDs change)
  useEffect(() => {
    let cancelled = false
    const BUCKET = 'user-audio'

    const loadExisting = async () => {
      try {
        if (!lessonNoteId || !lessonToolId) return

        // Ensure we have a session so Storage.list/signedUrl work
        const { data: sess } = await supabase.auth.getSession()
        if (!sess?.session?.user?.id) {
          try {
            window.parent?.postMessage({ type: 'mms:requestSession' }, parentOriginRef.current)
          } catch {}
          setTimeout(loadExisting, 600)
          return
        }

        // List files under the note folder and find `${lessonToolId}.*`
        const { data: listing, error: listErr } = await supabase
          .storage.from(BUCKET)
          .list(`${lessonNoteId}`, { limit: 100 })
        if (listErr) {
          console.warn('[Recorder] list error:', listErr)
          return
        }

        const file = (listing || []).find(f => f.name.startsWith(`${lessonToolId}.`))
        if (!file) return

        const objectKey = `${lessonNoteId}/${file.name}`
        const { data: signed, error: signErr } = await supabase
          .storage.from(BUCKET)
          .createSignedUrl(objectKey, 3600)
        if (signErr) {
          console.warn('[Recorder] signed url error:', signErr)
          return
        }

        if (!cancelled) {
          setUpload(prev => ({
            ...prev,
            isUploading: false,
            isUploaded: true,
            error: null,
            signedUrl: signed?.signedUrl || null
          }))
        }
      } catch (e) {
        console.warn('[Recorder] loadExisting error:', e)
      }
    }

    loadExisting()
    return () => { cancelled = true }
  }, [lessonNoteId, lessonToolId])

  if (!recorder.isSupported) {
    return (
      <div ref={containerRef} className="w-full bg-white border rounded-lg p-4">
        <div className="text-center">
          <FiMic className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">
            Audio recording is not supported in this browser.
          </p>
          <p className="text-xs text-gray-500">
            Please use Chrome, Firefox, or Edge for the best experience.
          </p>
        </div>
      </div>
    )
  }

  const canSave = recorder.blob && !upload.isUploading && recorder.blob.size <= 20 * 1024 * 1024
  const isRecording = recorder.state === 'recording'
  const isPaused = recorder.state === 'paused'
  const hasRecording = recorder.state === 'stopped' || recorder.blob

  return (
    <div ref={containerRef} className="w-full bg-white border rounded-lg">
      {/* Main Controls */}
      <div className="flex items-center gap-3 p-4">
        {/* Record/Stop Button */}
        <motion.button
          onClick={isRecording ? recorder.stop : recorder.start}
          disabled={upload.isUploading}
          whileTap={{ scale: 0.95 }}
          className={`
            flex items-center justify-center w-10 h-10 rounded-full transition-colors focus-ring
            ${isRecording 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
            }
            ${upload.isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          {isRecording ? <FiSquare className="h-4 w-4" /> : <FiMic className="h-4 w-4" />}
        </motion.button>

        {/* Pause/Resume (only show when recording or paused) */}
        {(isRecording || isPaused) && (
          <button
            onClick={isPaused ? recorder.resume : recorder.pause}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 focus-ring"
            aria-label={isPaused ? 'Resume recording' : 'Pause recording'}
          >
            {isPaused ? <FiPlay className="h-3 w-3" /> : <FiPause className="h-3 w-3" />}
          </button>
        )}

        {/* Duration */}
        <div className="flex items-center gap-2 flex-1">
          <span className="text-sm font-mono text-gray-700">
            {formatDuration(recorder.duration)}
          </span>
          
          {/* Audio Level Meter */}
          {isRecording && (
            <div className="flex-1 max-w-20 bg-gray-200 rounded-full h-2">
              <motion.div
                className="bg-green-500 h-2 rounded-full"
                style={{ width: `${recorder.audioLevel * 100}%` }}
                transition={{ type: "tween", duration: 0.1 }}
              />
            </div>
          )}
        </div>

        {/* File Size */}
        {(isRecording || hasRecording) && (
          <span className={`text-xs ${
            recorder.fileSize > 20 * 1024 * 1024 ? 'text-red-600' : 'text-gray-500'
          }`}>
            {formatFileSize(recorder.fileSize)}
          </span>
        )}
      </div>

      {/* Error Display */}
      {(recorder.error || upload.error) && (
        <div className="px-4 pb-2">
          <div className="text-sm text-red-600 bg-red-50 rounded p-2">
            {recorder.error || upload.error}
          </div>
        </div>
      )}

      {/* Preview & Actions */}
      {hasRecording && (
        <div className="border-t bg-gray-50 p-4">
          <div className="flex items-center gap-3">
            {/* Audio Preview */}
            {recorder.blob && (
              <div className="flex-1">
                <audio
                  controls
                  src={upload.signedUrl || URL.createObjectURL(recorder.blob)}
                  className="w-full h-8"
                  style={{ maxWidth: '200px' }}
                />
                <div className="text-xs text-gray-500 mt-1">
                  {recorder.mimeType.split(';')[0]} • {formatFileSize(recorder.blob.size)}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              {upload.isUploaded ? (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <FiUpload className="h-4 w-4" />
                  Saved
                </div>
              ) : (
                <>
                  <button
                    onClick={uploadRecording}
                    disabled={!canSave}
                    className={`
                      flex items-center gap-1 px-3 py-1 rounded text sm font-medium focus-ring
                      ${canSave 
                        ? 'bg-green-600 hover:bg-green-700 text-white' 
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }
                    `}
                  >
                    {upload.isUploading ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                    ) : (
                      <FiSave className="h-3 w-3" />
                    )}
                    {upload.isUploading ? 'Saving...' : 'Save'}
                  </button>

                  <button
                    onClick={recorder.reset}
                    disabled={upload.isUploading}
                    className="flex items-center gap-1 px-3 py-1 rounded text-sm font-medium bg-gray-200 hover:bg-gray-300 text-gray-700 focus-ring"
                  >
                    <FiTrash2 className="h-3 w-3" />
                    Discard
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Hint */}
      <div className="px-4 pb-2">
        <div className="text-xs text-gray-400 text-center">
          Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">R</kbd> to record/stop
        </div>
      </div>
    </div>
  )
}